// Satellite overview — Phase 2. Per-zone index history with a zone picker.
// For the course-wide anomaly view + per-date scrubbing, open Sat Mode on
// the Course Map; this page is the deep-dive single-zone timeline.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Zone } from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import { PageHeader } from '../components/ui/page-header';
import { SatelliteTimeline } from '../components/SatelliteTimeline.js';

interface CourseResponse { zones: Zone[] }

export function SatellitePage() {
  const { courseSlug } = useParams();
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseSlug) return;
    api
      .get<CourseResponse>(`/courses/${courseSlug}`)
      .then((r) => {
        const eligible = r.zones.filter((z) => z.zoneType === 'green' || z.zoneType === 'fairway');
        setZones(eligible);
        setSelectedZoneId(eligible[0]?.id ?? null);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [courseSlug]);

  if (!courseSlug) return null;
  return (
    <div className="max-w-6xl p-6 md:p-10">
      <PageHeader
        eyebrow="Phase 2"
        title="Satellite"
        description="Sentinel-2 vegetation indices (NDVI / NDRE / NDWI + soil-adjusted variants) over time, per zone. For the course-wide anomaly overlay and date scrubber, open Sat Mode on the Course Map."
      />
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="text-[10px] uppercase tracking-[0.15em] text-fg-muted font-semibold">
          Zone
        </label>
        <select
          value={selectedZoneId ?? ''}
          onChange={(e) => setSelectedZoneId(Number(e.currentTarget.value))}
          className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg"
        >
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name} · {z.zoneType.replace('_', ' ')}
            </option>
          ))}
        </select>
        <Link
          to={`/app/${courseSlug}/map`}
          className="ml-auto text-xs font-semibold text-greeniq-400 hover:underline"
        >
          Open Sat Mode on Course Map →
        </Link>
      </div>
      {error && <p className="mt-4 text-sm text-[color:var(--color-error)]">{error}</p>}
      <div className="mt-4">
        {selectedZoneId !== null && (
          <SatelliteTimeline courseSlug={courseSlug} zoneId={selectedZoneId} days={180} />
        )}
        {zones.length === 0 && !error && (
          <p className="text-sm text-fg-muted">Loading zones…</p>
        )}
      </div>
    </div>
  );
}
